import type { FastifyRequest, FastifyReply } from 'fastify'
import type { UsersService } from './users.service'
import { UpdateLocationSchema } from './users.schemas'
import { ApiResponse } from '../../utils/response'
import { createLogger } from '../../common/utils/logger.js'

const logger = createLogger('UsersController')

function getUserId(request: FastifyRequest): string | undefined {
  const user = request.user as { uid?: string; id?: string } | undefined
  return user?.uid ?? user?.id
}

export class UsersController {
  constructor(private readonly usersService: UsersService) {}

  async getUserStats(request: FastifyRequest, reply: FastifyReply) {
    try {
      const userId = getUserId(request)
      if (!userId) {
        logger.warn('Unauthorized user stats request')
        return ApiResponse.unauthorized(reply)
      }
      logger.debug('Fetching user stats', { userId })
      const stats = await this.usersService.calculateUserStats(userId)
      logger.info('User stats fetched', { userId })
      return reply.send(stats)
    } catch (error) {
      logger.error('Failed to get user stats', { error: (error as Error).message })
      request.log.error({ err: error }, 'users_get_stats_failed')
      return ApiResponse.internalError(reply)
    }
  }

  async updateLocation(request: FastifyRequest, reply: FastifyReply) {
    try {
      const userId = getUserId(request)
      if (!userId) {
        logger.warn('Unauthorized location update request')
        return ApiResponse.unauthorized(reply)
      }

      logger.debug('Updating user location', { userId })
      const parsed = UpdateLocationSchema.safeParse(request.body)
      if (!parsed.success) {
        logger.warn('Invalid location payload', { userId })
        return reply.status(400).send({
          error: 'Invalid location payload',
          details: parsed.error.flatten()
        })
      }

      const summary = await this.usersService.updateUserLocation(userId, {
        ...parsed.data,
        source: parsed.data.source ?? 'web'
      })
      logger.info('User location updated', { userId })

      return reply.status(201).send(summary)
    } catch (error) {
      logger.error('Failed to update user location', { error: (error as Error).message })
      request.log.error({ err: error }, 'users_update_location_failed')
      return ApiResponse.internalError(reply)
    }
  }

  async getLocationSummary(request: FastifyRequest, reply: FastifyReply) {
    try {
      const userId = getUserId(request)
      if (!userId) {
        logger.warn('Unauthorized location summary request')
        return ApiResponse.unauthorized(reply)
      }

      logger.debug('Fetching user location summary', { userId })
      const summary = await this.usersService.getUserLocationSummary(userId)
      logger.info('User location summary fetched', { userId })
      return reply.send(summary)
    } catch (error) {
      logger.error('Failed to get user location summary', { error: (error as Error).message })
      request.log.error({ err: error }, 'users_get_location_summary_failed')
      return ApiResponse.internalError(reply)
    }
  }
}
